import {h, cloneElement} from 'preact'
import {Children, forwardRef, useEffect} from 'preact/compat'
import {Column, Box} from '@dash-ui/react-layout'
import {useControls} from '@accessible/drawer'
import {useLocation} from 'wouter'
import css from 'minify-css.macro'
import clsx from 'clsx'
import {styles, mq} from '../styles'
import {Link} from './Link'
import {Text} from './Text'
import type {ColumnProps} from '@dash-ui/react-layout'

export const MainMenu = forwardRef<HTMLDivElement, MainMenuProps>(
  ({variant = 'inline', className, ...props}, ref) => {
    const {close} = useControls()
    const [location] = useLocation()

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(close, [location])

    return (
      <Column
        as='nav'
        ref={ref}
        {...props}
        className={clsx(className, mainMenu(variant))}
      >
        <Box pad={['none', 'lg']}>
          <Text stylex={['label', 'text/accent']}>Packages</Text>
        </Box>
        <MenuItem section='styles'>
          <SubmenuItem section=''>Getting started</SubmenuItem>
          <SubmenuItem section='api'>API docs</SubmenuItem>
          <SubmenuItem section='patterns'>Patterns</SubmenuItem>
          <SubmenuItem section='typescript'>TypeScript</SubmenuItem>
        </MenuItem>
        <MenuItem section='react'>
          <SubmenuItem section=''>Getting started</SubmenuItem>
          <SubmenuItem section='api'>API docs</SubmenuItem>
          <SubmenuItem section='typescript'>TypeScript</SubmenuItem>
        </MenuItem>
        <MenuItem section='react-layout'>
          <SubmenuItem section=''>Getting started</SubmenuItem>
          <SubmenuItem section='api'>API docs</SubmenuItem>
          <SubmenuItem section='typescript'>TypeScript</SubmenuItem>
        </MenuItem>
        <MenuItem section='spacing'>
          <SubmenuItem section=''>Getting started</SubmenuItem>
          <SubmenuItem section='api'>API docs</SubmenuItem>
          <SubmenuItem section='typescript'>TypeScript</SubmenuItem>
        </MenuItem>
        <MenuItem section='grid'>
          <SubmenuItem section=''>Getting started</SubmenuItem>
          <SubmenuItem section='api'>API docs</SubmenuItem>
          <SubmenuItem section='typescript'>TypeScript</SubmenuItem>
        </MenuItem>
        <MenuItem section='mq'>
          <SubmenuItem section=''>Getting started</SubmenuItem>
          <SubmenuItem section='api'>API docs</SubmenuItem>
          <SubmenuItem section='typescript'>TypeScript</SubmenuItem>
        </MenuItem>
        <MenuItem section='transition'>
          <SubmenuItem section=''>Getting started</SubmenuItem>
          <SubmenuItem section='api'>API docs</SubmenuItem>
          <SubmenuItem section='typescript'>TypeScript</SubmenuItem>
        </MenuItem>
        <MenuItem section='jest'>
          <SubmenuItem section=''>Getting started</SubmenuItem>
          <SubmenuItem section='api'>API docs</SubmenuItem>
        </MenuItem>
      </Column>
    )
  }
)

const mainMenu = styles({
  default: ({color}) => css`
    background-color: ${color['ui/primary']};
  `,
  inline: '',
  drawer: ({color, pad, transition, vh, z}) => css`
    overflow: auto;
    height: calc(${vh} - 64px);
    border-top: 2px solid ${color['intention/info']};
    padding: ${pad.md} 0;
    z-index: ${z.max};
    opacity: 0;
    transition-property: transform, opacity, visibility;
    transition-duration: ${transition.duration.primary};
    transition-timing-function: ${transition.timing.move};

    &[aria-hidden='false'] {
      opacity: 1;
    }
  `,
})

export interface MainMenuProps extends ColumnProps {
  variant?: 'drawer' | 'inline'
}

const MenuItem: React.FC<{section: string; match?: RegExp}> = ({
  section,
  match,
  children = null,
}) => {
  const href = '/' + section
  const [location] = useLocation()
  const isActive = location.match(match || new RegExp(`^(${href}$|${href}/)`))
  const submenuElements = (
    <Column className={submenuItems({active: Boolean(isActive)})}>
      {Children.map(children, (child: any) => {
        const childHref = [href, child.props.section].filter(Boolean).join('/')
        return cloneElement(child, {
          key: childHref,
          href: childHref,
          className: submenuItem({
            active: location === childHref,
          }),
        })
      })}
    </Column>
  )

  return (
    <Column className={menuItem(isActive ? 'active' : 'inactive')}>
      {isActive ? (
        <Link href={href}>@dash-ui/{section}</Link>
      ) : (
        <Link href={href}>
          <span>@dash-ui/</span>
          {section}
        </Link>
      )}

      {submenuElements}
    </Column>
  )
}

const SubmenuItem: React.FC<
  React.HTMLAttributes<HTMLAnchorElement> & {section: string}
> = ({section, ...props}) => <Link href={section} {...props} />

const menuItem = styles({
  default: ({transition, color}) => css`
    transition-property: background-color;
    transition-duration: ${transition.duration.primary};
    transition-timing-function: ${transition.timing.accelerate};
    backface-visibility: hidden;
    padding: 0;
    background-color: ${color['ui/primary']};
    white-space: nowrap;

    a {
      text-decoration: none;
      transform-origin: top left;
    }

    & > a:first-child {
      font-weight: 500;
    }
  `,
  inactive: mq({
    default: ({color}) => css`
      & > a:first-child {
        color: ${color['text/primary']};

        span:first-child {
          color: ${color['text/accent']};
        }
      }
    `,
    phone: ({pad, font}) => css`
      & > a:first-child {
        font-size: ${font.size.content};
        line-height: ${font.leading.content};
        letter-spacing: ${font.tracking.content};
        padding: ${pad.sm} ${pad.lg} ${pad.sm};
      }
    `,
    tablet: ({pad, font}) => css`
      & > a:first-child {
        font-size: ${font.size.contentSm};
        line-height: ${font.leading.contentSm};
        letter-spacing: ${font.tracking.contentSm};
        padding: ${pad.xs} ${pad.lg} ${pad.xs};
      }
    `,
  }),
  active: ({color, pad, font, radius}) => css`
    color: ${color['text/primary']};
    text-decoration: none;
    margin: 0 ${pad.md} ${pad.xs};
    padding: ${pad.md};
    background-color: ${color['intention/info']};
    border-radius: ${radius.primary};

    &:not(:first-of-type) {
      margin: ${pad.xs} ${pad.md} ${pad.xs};
    }

    & > a:first-child {
      font-size: ${font.size.contentSm};
      line-height: ${font.leading.contentSm};
      letter-spacing: ${font.tracking.contentSm};
      color: ${color['intention/infoInverse']};
    }
  `,
})

const submenuItem = styles({
  default: mq({
    default: ({color, font}) => css`
      text-decoration: none;
      font-weight: 400;
      color: ${color['text/accent']};
      font-size: ${font.size.content};
      line-height: ${font.leading.content};
      letter-spacing: ${font.tracking.content};
    `,
    phone: ({gap}) => css`
      padding: ${gap.md} 0 0;
    `,
    tablet: ({gap}) => css`
      padding: ${gap.sm} 0 0;
    `,
  }),
  active: ({color}) => css`
    font-weight: 500;
    color: ${color['text/primary']};
  `,
})

const submenuItems = styles({
  default: css`
    max-height: 0;
    overflow: hidden;

    & > * {
      display: block;
    }
  `,
  active: mq({
    default: ({gap}) => css`
      margin-top: ${gap.sm};
      max-height: none;
    `,
  }),
})
